import { CURRENCIES, DATE_FORMATS, LANGUAGES, type LngLat, type UpdateOrganizationInput } from '@chukua/common';
import { Form, Input, InputNumber, Select } from 'antd';
import { CompanyLocationField } from './CompanyLocationField';

const LANGUAGE_LABELS: Record<(typeof LANGUAGES)[number], string> = { en: 'English', sw: 'Swahili' };

export interface CompanySettingsFormValues {
  name: string;
  legalName?: string;
  kraPin?: string;
  address?: string;
  location: LngLat | null;
  currency: UpdateOrganizationInput['currency'];
  timezone: string;
  dateFormat: UpdateOrganizationInput['dateFormat'];
  language: UpdateOrganizationInput['language'];
  jobDueSoonThresholdDays: number;
  creditOverdueThresholdDays: number;
  driverRestHours: number;
}

/** Who the company is: short fields paired, two to a row, so the card stays short. */
export function ParticularsFields() {
  return (
    <div className="grid gap-x-4 sm:grid-cols-2">
      <Form.Item name="name" label="Company name" rules={[{ required: true, message: 'Enter a company name' }]}>
        <Input autoFocus />
      </Form.Item>
      <Form.Item name="legalName" label="Legal name">
        <Input />
      </Form.Item>
      <Form.Item name="kraPin" label="KRA PIN">
        <Input />
      </Form.Item>
      <Form.Item name="currency" label="Reporting currency" rules={[{ required: true }]}>
        <Select options={CURRENCIES.map((currency) => ({ value: currency, label: currency }))} />
      </Form.Item>
      <Form.Item name="address" label="Address" className="mb-0 sm:col-span-2">
        <Input.TextArea rows={2} />
      </Form.Item>
    </div>
  );
}

/** How dates, times and text read across the app: three short fields on one row. */
export function LocaleFields() {
  return (
    <div className="grid gap-x-4 sm:grid-cols-3">
      <Form.Item name="timezone" label="Timezone" className="mb-0" rules={[{ required: true, message: 'Enter a timezone' }]}>
        <Input placeholder="Africa/Nairobi" />
      </Form.Item>
      <Form.Item name="dateFormat" label="Date format" className="mb-0" rules={[{ required: true }]}>
        <Select options={DATE_FORMATS.map((dateFormat) => ({ value: dateFormat, label: dateFormat }))} />
      </Form.Item>
      <Form.Item name="language" label="Default language" className="mb-0" rules={[{ required: true }]}>
        <Select options={LANGUAGES.map((language) => ({ value: language, label: LANGUAGE_LABELS[language] }))} />
      </Form.Item>
    </div>
  );
}

/** Where the company is, on its own card beside the particulars. */
export function LocationFields() {
  return (
    <Form.Item name="location" className="mb-0">
      <CompanyLocationField />
    </Form.Item>
  );
}

/** Tenant-wide thresholds the jobs screens read. */
export function JobsFields() {
  return (
    <div className="grid gap-x-4 sm:grid-cols-2">
      <Form.Item
        name="jobDueSoonThresholdDays"
        label="Job due-soon threshold (days)"
        className="mb-0"
        rules={[{ required: true, message: 'Enter a number of days' }]}
        extra="How close a job's delivery deadline must be before the jobs list flags it as due soon."
      >
        <InputNumber className="w-full" min={1} max={365} />
      </Form.Item>
      <Form.Item
        name="creditOverdueThresholdDays"
        label="Credit overdue threshold (days)"
        className="mb-0"
        rules={[{ required: true, message: 'Enter a number of days' }]}
        extra="How many days overdue a customer must be before the credit check at job confirmation treats them as badly overdue."
      >
        <InputNumber className="w-full" min={1} max={365} />
      </Form.Item>
    </div>
  );
}

/** Tenant-wide driver rules: the rest window after a trip before the availability status flips back (gitaumoses4/chukua#54). */
export function DriversFields() {
  return (
    <Form.Item
      name="driverRestHours"
      label="Driver rest period (hours)"
      className="mb-0"
      rules={[{ required: true, message: 'Enter a number of hours' }]}
      extra="How long a driver shows as resting after a trip is closed out, before they are available again. 0 means no rest period."
    >
      <InputNumber className="w-full sm:max-w-48" min={0} max={72} precision={0} />
    </Form.Item>
  );
}
